import React, { useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Upload, message, Popconfirm } from 'antd'
import { UploadOutlined, DeleteOutlined, PaperClipOutlined } from '@ant-design/icons'
import { inject, observer } from 'mobx-react'
import UploadFileItem from '../Common/FileItemComponent/UploadFileItem'
import utils from '../../utils'
import styled from 'styled-components'

const { TextArea } = Input

const ExistingFileList = styled.ul`
  margin: 10px 0 15px;
  padding-left: 0 !important;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    margin-bottom: 6px;
    background: #f5f5f5;
    border-radius: 4px;
    border: 1px solid #e8e8e8;

    .file-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-right: 8px;
    }
  }
`

const ReplyMessageEditModal = props => {
  const {
    isVisible,
    handleCancel,
    comment,
    internalMessageStore,
    fileStore,
    loadingAnimationStore,
    isReceive,
    parentCode,
  } = props

  const [form] = Form.useForm()
  const [existingFiles, setExistingFiles] = useState([])
  const [newFileList, setNewFileList] = useState([])

  useEffect(() => {
    if (isVisible && comment) {
      form.setFieldsValue({ content: comment.content || '' })
      setExistingFiles(comment.file_ids || [])
      setNewFileList([])
    }
  }, [isVisible, comment])

  const handleRemoveExistingFile = fileId => {
    setExistingFiles(prev => prev.filter(f => f.file_id !== fileId))
  }

  const handleChangeNewFile = info => {
    const filteredFileList = info.fileList.filter(
      (elem, index, fileArray) =>
        fileArray.findIndex(file => file.name === elem.name) === index
    )
    setNewFileList(filteredFileList)
  }

  const handleRemoveNewFile = fileUID => {
    setNewFileList(prev => prev.filter(file => file.uid !== fileUID))
  }

  const handleFinish = async values => {
    try {
      loadingAnimationStore.showSpinner(true)

      // 1. Upload new files if any
      const filesToUpload = newFileList
        ? newFileList.map(f => f.originFileObj).filter(Boolean)
        : []
      const uploadPromises = filesToUpload.map(file => {
        const formData = new FormData()
        formData.append('file', file)
        return fileStore.uploadFile(formData)
      })

      const uploadResponses = await Promise.all(uploadPromises)
      const newlyUploadedFileIds = uploadResponses.map(res => res.data.file_id)

      // 2. Combine remaining existing file IDs + newly uploaded file IDs
      const finalFileIds = [
        ...existingFiles.map(f => f.file_id),
        ...newlyUploadedFileIds,
      ]

      // 3. Update comment via store
      await internalMessageStore.updateContent(
        comment.code,
        values.content,
        finalFileIds
      )

      // 4. Refresh internal message detail
      if (isReceive) {
        await internalMessageStore.getReceiveMessageById(parentCode)
      } else {
        await internalMessageStore.getSendMessageById(parentCode)
      }

      message.success('Cập nhật phản hồi thành công!')
      handleCancel()
    } catch (error) {
      console.error(error)
      message.error(error?.vi || error?.message || 'Cập nhật phản hồi thất bại!')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  return (
    <Modal
      title="Chỉnh sửa phản hồi"
      visible={isVisible}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={700}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="content"
          label="Nội dung phản hồi"
          rules={[{ required: true, message: 'Vui lòng nhập nội dung phản hồi!' }]}>
          <TextArea rows={4} placeholder="Nội dung phản hồi" />
        </Form.Item>

        {existingFiles.length > 0 && (
          <Form.Item label="Tệp đính kèm hiện tại">
            <ExistingFileList>
              {existingFiles.map(file => (
                <li key={file.file_id}>
                  <span className="file-name">
                    <PaperClipOutlined style={{ marginRight: 6 }} />
                    {file.name}
                  </span>
                  <Popconfirm
                    title="Bạn có chắc chắn muốn gỡ tệp này?"
                    okText="Gỡ"
                    cancelText="Hủy"
                    onConfirm={() => handleRemoveExistingFile(file.file_id)}>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </li>
              ))}
            </ExistingFileList>
          </Form.Item>
        )}

        {newFileList.length > 0 && (
          <Form.Item label="Tệp mới chuẩn bị tải lên">
            <ExistingFileList>
              {newFileList.map(file => (
                <UploadFileItem
                  key={file.uid}
                  file_id={file.uid}
                  file_name={file.name}
                  file_type={utils.getExtensionFile(file.name)}
                  handleRemoveFileFromUploadList={() =>
                    handleRemoveNewFile(file.uid)
                  }
                />
              ))}
            </ExistingFileList>
          </Form.Item>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
          }}>
          <Upload
            valuePropName="fileList"
            fileList={newFileList}
            multiple={true}
            onChange={handleChangeNewFile}
            beforeUpload={() => false}
            showUploadList={false}>
            <Button htmlType="button">
              <UploadOutlined /> Tải thêm tệp
            </Button>
          </Upload>

          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={handleCancel}>Hủy</Button>
            <Button type="primary" htmlType="submit">
              Lưu
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}

export default inject(
  'internalMessageStore',
  'fileStore',
  'loadingAnimationStore'
)(observer(ReplyMessageEditModal))
